import { Check, Lock, Sparkles } from 'lucide-react';
import RichView from '@/components/estimating/agreements/preview/RichView';
import { usd } from '@/components/estimating/agreements/agreementFormat';

function Items({ items, itemized, tid }) {
  return (
    <ul className="divide-y divide-[#EEF0F2] px-4 pb-2 sm:px-5">
      {items.map((it, j) => (
        <li key={j} className="flex items-start justify-between gap-4 py-2.5" data-testid={`${tid}-item-${j}`}>
          <div className="min-w-0 flex-1">
            {it.name && <p className="text-[13px] font-semibold text-[#0D1B2A]" data-testid={`${tid}-item-${j}-name`}>{it.name}</p>}
            {it.wording && <RichView html={it.wording} className="text-[13px] leading-relaxed text-[#3A3F47]" />}
          </div>
          {itemized && it.price_cents != null && (
            <div className="shrink-0 text-right">
              <p className="text-[13px] font-semibold tabular-nums text-[#0D1B2A]" data-testid={`${tid}-item-${j}-price`}>{usd(it.price_cents)}</p>
              {it.unit_price_cents != null && <p className="text-[11px] tabular-nums text-[#5C6370]" data-testid={`${tid}-item-${j}-each`}>{it.qty} {it.unit} × {usd(it.unit_price_cents)}</p>}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

const Box = ({ on, color }) => (
  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[5px] border-2 transition-colors duration-150"
    style={on ? { background: color, borderColor: color } : { borderColor: '#AEB4BD', background: '#fff' }}>
    {on && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
  </span>
);

function Title({ s, on, isNew, tid, hfont, pickable }) {
  return (
    <>
      <span className="min-w-0 flex-1">
        <span className="block font-exo text-base font-bold leading-snug text-[#0D1B2A]" style={hfont} data-testid={`${tid}-name`}>{s.name || 'Service'}</span>
        <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.1em]">
          {s.optional
            ? <span className="text-[#5C6370]" data-testid={`${tid}-state`}>{!pickable ? 'Optional · not in the total' : on ? 'Optional · added' : 'Optional · tap to add'}</span>
            : <span className="inline-flex items-center gap-1 text-[#4A7C59]" data-testid={`${tid}-state`}><Lock className="h-3 w-3" />Included</span>}
          {isNew && <span className="inline-flex items-center gap-1 rounded-full bg-[#F7EFD9] px-2 py-0.5 text-[#9C7B36]" data-testid={`${tid}-new`}><Sparkles className="h-3 w-3" />New since your picks</span>}
        </span>
      </span>
      {s.total_cents != null && <span className={`shrink-0 font-exo text-base font-bold tabular-nums transition-colors ${on ? 'text-[#0D1B2A]' : 'text-[#8A8F99]'}`} data-testid={`${tid}-total`}>{usd(s.total_cents)}</span>}
    </>
  );
}

// One service: a Required one is locked in; an Optional one is a big tap target that ticks it on or off (`onToggle`
// null: view only, it's listed with its own price, outside the total).
export default function ProposalService({ s, i, on, isNew, itemized, onToggle, color, hfont }) {
  const tid = `proposal-service-${i}`;
  const head = <Title s={s} on={on} isNew={isNew} tid={tid} hfont={hfont} pickable={!!onToggle} />;
  return (
    <article className={`overflow-hidden rounded-md border bg-white transition-[border-color,box-shadow] duration-200 ${s.optional ? (on ? 'shadow-sm' : 'border-dashed') : 'border-[#D5D8DD]'}`}
      style={s.optional ? { borderColor: on ? color : '#C3C8D0' } : undefined} data-testid={tid} data-included={on ? 'yes' : 'no'}>
      {s.optional && onToggle ? (
        <button type="button" role="checkbox" aria-checked={on} onClick={onToggle}
          className="flex min-h-[64px] w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[#F7F8FA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4575A8]/50 sm:px-5"
          data-testid={`${tid}-toggle`}><Box on={on} color={color} />{head}</button>
      ) : <div className="flex min-h-[56px] items-center gap-3 px-4 py-3 sm:px-5">{head}</div>}
      {s.note && <RichView html={s.note} className="px-4 pb-1 text-xs italic leading-relaxed text-[#5C6370] sm:px-5" tid={`${tid}-note`} />}
      <div className={on ? '' : 'opacity-60'}><Items items={s.items} itemized={itemized} tid={tid} /></div>
    </article>
  );
}
